import React from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api, API, scaricaFile } from "@/lib/api";
import { toast } from "sonner";
import { euro, num, STATI } from "@/lib/format";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { RiepilogoGiornaliero } from "@/components/RiepilogoGiornaliero";
import { PrestazioniCard } from "@/components/prestazioni/PrestazioniCard";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
} from "recharts";
import {
  TrendingUp,
  TrendingDown,
  Wallet,
  Receipt,
  Briefcase,
  AlertTriangle,
  FileDown,
  FileSpreadsheet,
  X,
} from "lucide-react";

const StatCard = ({ icon: Icon, label, value, sub, tone = "slate", testid }) => {
  const tones = {
    slate: "text-slate-900",
    green: "text-emerald-600",
    red: "text-red-600",
    sky: "text-sky-600",
    amber: "text-amber-600",
  };
  return (
    <div
      className="bg-white border border-slate-200 rounded-xl shadow-sm p-5 stagger-in"
      data-testid={testid}
    >
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
          {label}
        </span>
        <Icon className={`h-5 w-5 ${tones[tone]}`} />
      </div>
      <div className={`mt-3 font-display text-2xl sm:text-3xl font-bold ${tones[tone]}`}>
        {value}
      </div>
      {sub && <div className="text-xs text-slate-400 mt-1">{sub}</div>}
    </div>
  );
};

export default function DashboardGenerale() {
  const navigate = useNavigate();
  const [da, setDa] = React.useState("");
  const [a, setA] = React.useState("");
  const { data, isLoading } = useQuery({
    queryKey: ["dashboard-generale", da, a],
    queryFn: async () =>
      (await api.get("/dashboard/generale", { params: { da: da || undefined, a: a || undefined } })).data,
  });

  const reportUrl = (fmt, kind = "commesse") => {
    const p = new URLSearchParams();
    if (da) p.set("da", da);
    if (a) p.set("a", a);
    const qs = p.toString();
    return `${API}/report/${kind}/${fmt}${qs ? `?${qs}` : ""}`;
  };

  if (isLoading || !data)
    return <div className="text-slate-400 py-20 text-center">Caricamento dashboard…</div>;

  const marginTone = data.margine_totale < 0 ? "red" : "green";

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
            Dashboard Generale
          </h1>
          <p className="text-slate-500 mt-1">
            Panoramica di tutte le commesse: fatturato, costi e margini.
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex flex-col">
            <label className="text-xs text-slate-400 mb-1">Dal</label>
            <Input type="date" value={da} onChange={(e) => setDa(e.target.value)} className="bg-white w-[150px]" data-testid="filtro-da" />
          </div>
          <div className="flex flex-col">
            <label className="text-xs text-slate-400 mb-1">Al</label>
            <Input type="date" value={a} onChange={(e) => setA(e.target.value)} className="bg-white w-[150px]" data-testid="filtro-a" />
          </div>
          {(da || a) && (
            <Button variant="ghost" size="icon" onClick={() => { setDa(""); setA(""); }} data-testid="filtro-azzera" className="text-slate-500">
              <X className="h-4 w-4" />
            </Button>
          )}
          <Button variant="outline" onClick={() => scaricaFile(reportUrl("pdf"), (m) => toast.error(m))} data-testid="report-pdf-button">
            <FileDown className="h-4 w-4 mr-1.5" /> Report PDF
          </Button>
          <Button variant="outline" onClick={() => scaricaFile(reportUrl("excel"), (m) => toast.error(m))} data-testid="report-excel-button">
            <FileSpreadsheet className="h-4 w-4 mr-1.5" /> Report Excel
          </Button>
          <div className="flex items-center rounded-lg border border-orange-200 bg-orange-50 overflow-hidden">
            <span className="px-3 text-xs font-semibold text-primary uppercase tracking-wider hidden sm:inline">Per cliente</span>
            <Button variant="ghost" size="sm" className="rounded-none text-primary hover:bg-orange-100" onClick={() => scaricaFile(reportUrl("pdf", "clienti"), (m) => toast.error(m))} data-testid="report-clienti-pdf-button">
              <FileDown className="h-4 w-4 mr-1" /> PDF
            </Button>
            <Button variant="ghost" size="sm" className="rounded-none text-primary hover:bg-orange-100" onClick={() => scaricaFile(reportUrl("excel", "clienti"), (m) => toast.error(m))} data-testid="report-clienti-excel-button">
              <FileSpreadsheet className="h-4 w-4 mr-1" /> Excel
            </Button>
          </div>
        </div>
      </div>

      <RiepilogoGiornaliero />

      <PrestazioniCard />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 sm:gap-6">
        <StatCard
          icon={Wallet}
          label="Fatturato Totale"
          value={euro(data.totale_fatturato)}
          sub={`di cui ${euro(data.fatturato_incassato)} già fatturato`}
          tone="sky"
          testid="stat-fatturato"
        />
        <StatCard
          icon={Receipt}
          label="Da Fatturare"
          value={euro(data.da_fatturare_aperte || 0)}
          sub={`su ${num(data.num_da_fatturare || 0)} commesse non fatturate`}
          tone="amber"
          testid="stat-da-fatturare"
        />
        <StatCard
          icon={Receipt}
          label="Costi Totali"
          value={euro(data.totale_costi)}
          tone="slate"
          testid="stat-costi"
        />
        <StatCard
          icon={marginTone === "red" ? TrendingDown : TrendingUp}
          label="Margine Lordo"
          value={euro(data.margine_totale)}
          sub={`${num(data.margine_perc, 1)}% sul fatturato`}
          tone={marginTone}
          testid="stat-margine"
        />
        <StatCard
          icon={Briefcase}
          label="Commesse Attive"
          value={num(data.num_commesse)}
          sub={`${data.per_stato.aperta} aperte · ${data.per_stato.in_corso} in corso`}
          tone="slate"
          testid="stat-commesse"
        />
      </div>

      {data.andamento && data.andamento.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5" data-testid="chart-andamento">
          <h2 className="font-display text-lg font-semibold text-slate-800 mb-1">
            Andamento ricavi e margini
          </h2>
          <p className="text-xs text-slate-400 mb-4">Aggregato per mese di apertura commessa.</p>
          <div style={{ width: "100%", height: 300 }}>
            <ResponsiveContainer>
              <AreaChart data={data.andamento} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="gRicavi" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#F36621" stopOpacity={0.35} />
                    <stop offset="95%" stopColor="#F36621" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gMargine" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#15803D" stopOpacity={0.35} />
                    <stop offset="95%" stopColor="#15803D" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="mese" tick={{ fontSize: 12, fill: "#64748B" }} />
                <YAxis tick={{ fontSize: 12, fill: "#64748B" }} tickFormatter={(v) => `${v / 1000}k`} />
                <Tooltip
                  formatter={(v, name) => [euro(v), name === "ricavi" ? "Ricavi" : "Margine"]}
                  contentStyle={{ borderRadius: 12, border: "1px solid #E2E8F0", fontSize: 13 }}
                />
                <Legend
                  formatter={(v) => (v === "ricavi" ? "Ricavi" : "Margine")}
                  wrapperStyle={{ fontSize: 13 }}
                />
                <Area type="monotone" dataKey="ricavi" stroke="#F36621" strokeWidth={2} fill="url(#gRicavi)" />
                <Area type="monotone" dataKey="margine" stroke="#15803D" strokeWidth={2} fill="url(#gMargine)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {data.commesse_in_perdita.length > 0 && (
        <div
          className="bg-red-50 border border-red-200 rounded-xl p-5"
          data-testid="alert-in-perdita"
        >
          <div className="flex items-center gap-2 text-red-700 font-semibold">
            <AlertTriangle className="h-5 w-5" />
            Commesse in perdita ({data.commesse_in_perdita.length})
          </div>
          <div className="mt-3 space-y-2">
            {data.commesse_in_perdita.map((c) => (
              <button
                key={c.id}
                onClick={() => navigate(`/commesse/${c.id}`)}
                className="w-full flex items-center justify-between bg-white border border-red-100 rounded-lg px-4 py-2.5 text-left hover:border-red-300 transition-colors"
                data-testid={`perdita-row-${c.codice}`}
              >
                <div>
                  <span className="font-mono text-xs text-slate-500">{c.codice}</span>
                  <div className="text-sm font-medium text-slate-800">{c.descrizione}</div>
                </div>
                <span className="font-semibold text-red-600">{euro(c.margine)}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {data.commesse_preventivo_superato?.length > 0 && (
        <div className="bg-orange-50 border border-orange-300 rounded-xl p-5" data-testid="alert-preventivo-superato">
          <div className="flex items-center gap-2 text-orange-800 font-semibold">
            <AlertTriangle className="h-5 w-5" />
            Preventivo superato ({data.commesse_preventivo_superato.length})
          </div>
          <p className="text-xs text-orange-700/80 mt-0.5">Costi reali oltre il preventivo ma ancora in margine positivo.</p>
          <div className="mt-3 space-y-2">
            {data.commesse_preventivo_superato.map((c) => (
              <button
                key={c.id}
                onClick={() => navigate(`/commesse/${c.id}`)}
                className="w-full flex items-center justify-between bg-white border border-orange-100 rounded-lg px-4 py-2.5 text-left hover:border-orange-400 transition-colors"
                data-testid={`preventivo-superato-row-${c.codice}`}
              >
                <div>
                  <span className="font-mono text-xs text-slate-500">{c.codice}</span>
                  <div className="text-sm font-medium text-slate-800">{c.descrizione}</div>
                </div>
                <div className="text-right">
                  <div className="font-semibold text-orange-700">+{euro(c.scostamento_preventivo)}</div>
                  <div className="text-xs text-slate-400">prev. {euro(c.totale_stimato)}</div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100">
          <h2 className="font-display text-lg font-semibold text-slate-800">
            Tutte le commesse
          </h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-slate-400 border-b border-slate-100">
                <th className="px-5 py-3">Codice</th>
                <th className="px-5 py-3">Cliente / Descrizione</th>
                <th className="px-5 py-3">Stato</th>
                <th className="px-5 py-3 text-right">Fatturato</th>
                <th className="px-5 py-3 text-right">Costi</th>
                <th className="px-5 py-3 text-right">Margine</th>
              </tr>
            </thead>
            <tbody>
              {data.commesse.map((c) => (
                <tr
                  key={c.id}
                  onClick={() => navigate(`/commesse/${c.id}`)}
                  className="border-b border-slate-50 hover:bg-slate-50 cursor-pointer transition-colors"
                  data-testid={`dashboard-commessa-${c.codice}`}
                >
                  <td className="px-5 py-3 font-mono text-xs text-slate-600">{c.codice}</td>
                  <td className="px-5 py-3">
                    <div className="font-medium text-slate-800 flex items-center gap-1.5">
                      {c.cliente_nome}
                      {!c.in_perdita && c.preventivo_superato && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-orange-700 bg-orange-50 border border-orange-200 rounded-full px-1.5 py-0.5" data-testid={`badge-prev-superato-${c.codice}`}>
                          <AlertTriangle className="h-3 w-3" /> Prev. superato
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-slate-400">{c.descrizione}</div>
                  </td>
                  <td className="px-5 py-3">
                    <span
                      className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium border ${STATI[c.stato]?.cls}`}
                    >
                      {STATI[c.stato]?.label}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-right font-mono">{euro(c.importo)}</td>
                  <td className="px-5 py-3 text-right font-mono text-slate-500">
                    {euro(c.costi)}
                  </td>
                  <td
                    className={`px-5 py-3 text-right font-mono font-semibold ${
                      c.margine < 0 ? "text-red-600" : "text-emerald-600"
                    }`}
                  >
                    {euro(c.margine)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
